// Pure rules behind the wrap-up panel (Stage 2D). Tested in wrapUpLogic.test.ts.
import type { WrapUpInput, WrapUpReason, WrapUpSheetLine } from './wrapUpTypes'

export interface WrapUpDraft {
  used: number
  keep: number
  send_back: number
  throw_away: number
  reason: WrapUpReason
}

/** Used defaults to what the recipes say; nothing else is assumed. */
export function initialWrapUp(lines: WrapUpSheetLine[]): Record<string, WrapUpDraft> {
  return Object.fromEntries(lines.map((l) => [l.stock_item_id, { used: l.planned_use, keep: 0, send_back: 0, throw_away: 0, reason: 'leftover' as WrapUpReason }]))
}

/** What stays in the kitchen's books for this item after the wrap-up. */
export function leftAfter(onHand: number, d: WrapUpDraft): number {
  return Math.round((onHand - d.used - d.send_back - d.throw_away) * 1e5) / 1e5
}

export const needsPin = (drafts: Record<string, WrapUpDraft>) => Object.values(drafts).some((d) => d.throw_away > 1e-6)

export function wrapUpBody(drafts: Record<string, WrapUpDraft>, pin: string, note: string, acknowledgeOpenBatches = false): WrapUpInput {
  const lines = Object.entries(drafts)
    .filter(([, d]) => d.used + d.keep + d.send_back + d.throw_away > 1e-6)
    .map(([stock_item_id, d]) => ({
      stock_item_id, used: d.used, keep: d.keep, send_back: d.send_back, throw_away: d.throw_away,
      ...(d.throw_away > 1e-6 ? { reason: d.reason } : {}),
    }))
  return {
    lines,
    ...(needsPin(drafts) ? { pin: pin.trim() } : {}),
    ...(note.trim() ? { note: note.trim() } : {}),
    ...(acknowledgeOpenBatches ? { acknowledge_open_batches: true } : {}),
  }
}

/** The panel can be sent: at least one line (none needed when the sheet has
 *  nothing to ask about — an equipment-only plan), a PIN when throwing away,
 *  and no unfinished batch unless the chef said to go ahead (Batch 2). */
export function canSubmitWrapUp(
  drafts: Record<string, WrapUpDraft>,
  pin: string,
  opts: { sheetEmpty?: boolean; openBatches?: number; acknowledged?: boolean } = {},
): boolean {
  if ((opts.openBatches ?? 0) > 0 && !opts.acknowledged) return false
  if (wrapUpBody(drafts, pin, '').lines.length === 0 && !opts.sheetEmpty) return false
  return !needsPin(drafts) || pin.trim().length >= 4
}
